import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { useFlags } from '../contexts/FlagContext';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import FeaturedGameSpotlight from '../components/FeaturedGameSpotlight';
import { 
  Gamepad2, Sparkles, Zap, Box, Code2, Users, 
  ArrowRight, ChevronRight, Play, Wand2, Layers, Globe, Rocket
} from 'lucide-react';

const HERO_BG = `${process.env.REACT_APP_BACKEND_URL}/api/logokit/poster.png`;

export default function Landing() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t, language, setLanguage, LANGUAGES } = useI18n();
  const { flags } = useFlags();

  const features = [
    { icon: Sparkles, title: "AI Game Generation", description: "Describe your game in plain English. Our AI builds it in seconds." },
    { icon: Box, title: "Real-time 3D Editor", description: "Drag-and-drop interface with live preview. No coding required." },
    { icon: Wand2, title: "AI Copilot", description: "Chat with AI to modify your game. 'Add enemies that chase the player'" },
    { icon: Layers, title: "Asset Generation", description: "Generate sprites, textures, and 3D models with AI." },
    { icon: Code2, title: "Pro Mode", description: "Advanced controls and scripting for experienced developers." },
    { icon: Users, title: "Publish & Share", description: "One-click publishing. Share your games with the world." }
  ];

  const genres = [
    { name: "Platformer", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
    { name: "FPS", color: "bg-red-500/20 text-red-400 border-red-500/30" },
    { name: "RPG", color: "bg-purple-500/20 text-purple-400 border-purple-500/30" },
    { name: "Puzzle", color: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
    { name: "Simulation", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" }
  ];

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden flex flex-col" data-testid="landing-page">
      {/* Background effects — must not intercept clicks on real content */}
      <div className="absolute inset-0 pointer-events-none">
        <img 
          src={HERO_BG} 
          alt="" 
          className="absolute inset-0 w-full h-[800px] object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#050505]/80 to-[#050505]" />
      </div>
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      <div className="absolute inset-0 noise pointer-events-none" />

      {/* Header */}
      <header className="relative z-10 border-b border-[#1E1E24] glass">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#06B6D4] to-[#0891B2] flex items-center justify-center shadow-lg shadow-[#06B6D4]/20">
              <Gamepad2 className="w-5 h-5 text-white" />
            </div>
            <span className="font-black text-xl tracking-tight text-white">PlayMold</span>
          </div>

          <nav className="hidden md:flex items-center gap-6">
            <button onClick={() => navigate('/gallery')} className="text-sm text-zinc-400 hover:text-white transition-colors">Gallery</button>
            <button onClick={() => navigate('/pricing')} className="text-sm text-zinc-400 hover:text-white transition-colors">Pricing</button>
            <button onClick={() => navigate('/tutorial')} className="text-sm text-zinc-400 hover:text-white transition-colors">Tutorial</button>
            <button onClick={() => navigate('/faq')} className="text-sm text-zinc-400 hover:text-white transition-colors">FAQ</button>
          </nav>

          <div className="flex items-center gap-3">
            {/* Language selector */}
            <div className="hidden sm:flex items-center gap-1 text-zinc-500">
              <Globe className="w-4 h-4" />
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="bg-transparent text-sm border-none focus:outline-none cursor-pointer hover:text-white transition-colors"
              >
                {Object.entries(LANGUAGES).map(([code, { flag }]) => (
                  <option key={code} value={code} className="bg-[#1E1E24]">
                    {flag}
                  </option>
                ))}
              </select>
            </div>

            {isAuthenticated ? (
              <Button onClick={() => navigate('/dashboard')} className="bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90">
                Dashboard <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/login')} className="text-zinc-300 hover:text-white" data-testid="login-btn">
                  {t('signIn')}
                </Button>
                <Button onClick={() => navigate('/register')} className="bg-white text-black hover:bg-zinc-200 rounded-full px-6" data-testid="get-started-btn">
                  {t('getStarted') || 'Get Started'} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative z-10 pt-24 pb-32 px-6">
        <div className="max-w-[1400px] mx-auto">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#06B6D4]/10 border border-[#06B6D4]/30 mb-8">
              <Zap className="w-4 h-4 text-[#06B6D4]" />
              <span className="text-xs font-mono tracking-wider text-[#06B6D4]">{t('aiPowered')}</span>
            </div>

            {/* Headline */}
            <h1 className="text-5xl md:text-7xl font-black tracking-tighter text-white mb-6 leading-[0.95]">
              {t('heroTitle')}
              <br />
              <span className="text-[#06B6D4]">{t('heroTitleHighlight')}</span>
            </h1>

            <p className="text-lg text-zinc-400 mb-8 max-w-xl leading-relaxed">
              {t('heroSubtitle')}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 mb-12">
              <Button 
                size="lg" 
                className="bg-white text-black hover:bg-zinc-200 rounded-full px-8 group"
                onClick={() => navigate(isAuthenticated ? '/dashboard' : '/register')}
                data-testid="hero-cta"
              >
                {t('startCreating')}
                <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="btn-neon rounded-full px-8"
                onClick={() => navigate('/gallery')}
                data-testid="gallery-btn"
              >
                <Play className="mr-2 w-4 h-4" />
                {t('exploreGames')}
              </Button>
              <Button 
                size="lg" 
                variant="ghost"
                className="text-zinc-300 hover:text-white rounded-full px-6"
                onClick={() => navigate('/templates')}
                data-testid="templates-btn"
              >
                <Rocket className="mr-2 w-4 h-4" />
                Start from Template
              </Button>
            </div>

            {/* Play-as-guest banner — no-signup conversion nudge */}
            {!isAuthenticated && (
              <div className="inline-flex items-center gap-2 mb-8 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-xs text-emerald-300" data-testid="guest-play-hint">
                <span>✨</span>
                <span>No signup required to play or explore — just start.</span>
              </div>
            )}

            {/* Genres */}
            <div className="flex flex-wrap gap-2">
              {genres.map(genre => (
                <span key={genre.name} className={`px-3 py-1 text-xs font-mono border rounded-sm ${genre.color}`}>
                  {genre.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Demo Preview */}
      <section className="relative z-10 px-6 pb-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="rounded-sm border border-[#1E1E24] bg-[#0A0A0B] overflow-hidden">
            {/* Editor mockup header */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[#1E1E24] bg-[#121214]">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500/50" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/50" />
                <div className="w-3 h-3 rounded-full bg-green-500/50" />
              </div>
              <span className="text-xs font-mono text-zinc-500 ml-2">PlayMold Editor — My Awesome Game</span>
            </div>
            
            {/* Editor content */}
            <div className="grid grid-cols-12 h-[400px]">
              {/* Hierarchy panel */}
              <div className="col-span-2 border-r border-[#1E1E24] p-2">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-3">Scene</div>
                <div className="space-y-1 text-sm text-zinc-400">
                  <div className="hierarchy-item selected"><Box className="w-3 h-3" /> Player</div>
                  <div className="hierarchy-item"><Box className="w-3 h-3" /> Ground</div>
                  <div className="hierarchy-item"><Box className="w-3 h-3" /> Enemy</div>
                  <div className="hierarchy-item"><Sparkles className="w-3 h-3" /> Light</div>
                </div>
              </div>
              
              {/* Canvas */}
              <div className="col-span-7 bg-[#0A0A0B] canvas-grid flex items-center justify-center">
                <div className="text-center">
                  <div className="w-20 h-20 rounded-sm bg-[#06B6D4]/20 border border-[#06B6D4]/50 flex items-center justify-center mx-auto mb-4 animate-float">
                    <Gamepad2 className="w-10 h-10 text-[#06B6D4]" />
                  </div>
                  <p className="text-zinc-500 text-sm font-mono">3D Preview Canvas</p>
                </div>
              </div>
              
              {/* Properties & Copilot */}
              <div className="col-span-3 border-l border-[#1E1E24]">
                <div className="border-b border-[#1E1E24] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-3">Properties</div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-zinc-500">Position</span>
                      <span className="font-mono text-[#06B6D4]">0, 2, 0</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-zinc-500">Scale</span>
                      <span className="font-mono text-[#06B6D4]">1, 1, 1</span>
                    </div>
                  </div>
                </div>
                <div className="p-3">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-3">AI Copilot</div>
                  <div className="terminal rounded-sm p-2 text-xs">
                    <p className="opacity-50">{">"} Add a coin above the player</p>
                    <p className="text-[#22C55E] mt-1">✓ Added collectible "Coin" at (0, 4, 0)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Game of the Week */}
      {flags.show_featured_spotlight && (
        <section className="relative z-10 px-6 pb-24">
          <div className="max-w-[1400px] mx-auto">
            <FeaturedGameSpotlight variant="landing" />
          </div>
        </section>
      )}

      {/* Features Grid */}
      <section className="relative z-10 px-6 py-24 border-t border-[#1E1E24]">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black tracking-tighter text-white mb-4">
              Everything You Need
            </h2>
            <p className="text-zinc-400 max-w-xl mx-auto">
              From idea to playable game in minutes. No coding experience required.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((feature, idx) => (
              <div 
                key={feature.title}
                className="project-card p-6 animate-fade-in"
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                <div className="w-12 h-12 rounded-sm bg-[#06B6D4]/10 border border-[#06B6D4]/30 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-[#06B6D4]" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 px-6 py-24 border-t border-[#1E1E24]">
        <div className="max-w-[1400px] mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-black tracking-tighter text-white mb-6">
            Ready to Create?
          </h2>
          <p className="text-zinc-400 mb-8 max-w-xl mx-auto">
            Join thousands of creators making games with AI. Start building your first game today.
          </p>
          <Button 
            size="lg" 
            className="bg-white text-black hover:bg-zinc-200 rounded-full px-10"
            onClick={() => navigate(isAuthenticated ? '/dashboard' : '/register')}
          >
            Start Creating — It's Free
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <div className="relative z-10">
        <Footer />
      </div>

      {/* Copyright — MUST be the very last thing at the bottom */}
      <div className="relative z-10 border-t border-[#1E1E24] bg-black">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-2 text-center">
          <p className="text-sm text-zinc-200 font-medium" data-testid="landing-copyright">
            © {new Date().getFullYear()} PlayMold. All rights reserved.
          </p>
          <p className="text-xs text-zinc-400">Built with love in the browser · <a href="/privacy" className="text-zinc-300 hover:text-[#06B6D4]">Privacy</a> · <a href="/terms" className="text-zinc-300 hover:text-[#06B6D4]">Terms</a></p>
        </div>
      </div>
    </div>
  );
}
